/**
 * HtmlDocument — the ONE HTML shell, rendered by BOTH render paths.
 *
 * SSOT for the document structure: the server (`server/render.tsx` via
 * `renderToReadableStream`) and the client (`app/entry-client.tsx` via
 * `hydrateRoot(document, …)`) render this SAME component. Because React owns
 * `<html>`/`<head>` on both sides, React 19 hoists `<title>`/`<meta>`/`<link>`
 * (from `SeoHead`, `Image`'s `<link rel="preload">`, …) into THIS `<head>`
 * symmetrically — so a server/client structure mismatch (the DOM-duplication +
 * hydration-error class the old hand-written string shell caused, because it
 * streamed the app into `#root` under a `<head>` React couldn't hoist into) is
 * unrepresentable.
 *
 * NO `<script>` nodes live here: the state globals (`window.__CONFIG__` …) and
 * the client module are injected by React as bootstrap (`bootstrapScriptContent`
 * / `bootstrapModules`), which React excludes from hydration — so the client
 * never has to byte-reproduce them. The dynamic head bits the client needs to
 * render an identical `<head>` (css + modulepreloads + locale) arrive via
 * `window.__SHELL__` (see `server/bootstrap.ts`), typed as [`ShellManifest`].
 *
 * A lint (`no-raw-doc-shell`) bans hand-written `<html>`/`<head>`/`<body>` /
 * `id="root"` HTML anywhere else, so a parallel shell that can't hoist can't
 * come back.
 */
import type { ReactNode } from 'react';

/**
 * The per-render, build-derived head inputs the CLIENT must reproduce to render
 * a `<head>` identical to the server's (hydration match). Serialized into
 * `window.__SHELL__` by the server; read by `entry-client.tsx`. The static head
 * (charset/viewport/favicons/fonts) is constant and lives in the component.
 */
export interface ShellManifest {
  /** `<html lang>` — the request locale. */
  locale: string;
  /** Content-hashed stylesheet URLs (`/assets/*.css`). */
  cssFiles: string[];
  /** Content-hashed JS chunks to `<link rel="modulepreload">`. */
  preloadChunks: string[];
}

interface HtmlDocumentProps extends ShellManifest {
  /** The app subtree — `ShellProviders` wrapping the router (Static on the
   *  server, Browser on the client: the React Router hydration contract). */
  children: ReactNode;
}

/**
 * The full HTML document. `renderToReadableStream` emits `<!DOCTYPE html>` when
 * the root element is `<html>`. Title/meta/link are intentionally absent — they
 * are hoisted in from the app tree by React 19.
 */
export function HtmlDocument({ locale, cssFiles, preloadChunks, children }: HtmlDocumentProps) {
  return (
    <html lang={locale} dir="ltr">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/apple-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="theme-color" content="#18181b" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
        {cssFiles.map((href) => (
          <link key={href} rel="stylesheet" href={href} />
        ))}
        {preloadChunks.map((href) => (
          // `<link rel="modulepreload">` is a hint, not script/style — it carries
          // no CSP nonce, so the client can reproduce it from `__SHELL__` without
          // needing the server nonce.
          <link key={href} rel="modulepreload" href={href} />
        ))}
      </head>
      <body className="font-sans antialiased">
        <div id="root">{children}</div>
      </body>
    </html>
  );
}
